import { DataLayer } from '@amzn/aws-d2c-data-layer';
import { schemaVersion } from '@amzn/aws-d2c-data-layer-schema/dist/1.0/index';
import { JSONObject } from '@amzn/aws-d2c-data-layer/dist/types';
import { D2CClientRequestHandler } from '../clients/D2CClientRequestHandler';
import { v4 } from 'uuid';
import { BATCH_MANAGER_ENDPOINT } from '../constants/clients';
import { log, logErr } from '../utils/logger';
import { BatchManagerOptions, Batch, InflightBatch } from '../types/types';

export class BatchManager {
  private maxBatchSize: number;
  private batchingTimeMillis: number;
  private maxRetries: number;

  private failedBatchQueue: Array<Batch>;

  private timer: ReturnType<typeof setTimeout> | undefined;
  private D2CClientRequestHandler;

  constructor(opts: BatchManagerOptions) {
    this.maxBatchSize = opts.maxBatchSize || 10;
    this.batchingTimeMillis = opts.batchingTimeMillis || 2000;
    this.maxRetries = opts.maxRetries || 1;

    this.failedBatchQueue = [];

    // If we already have a visitorId, fire off the first batch immediately.
    // Otherwise, wait a full cycle in the hopes that we'll have one before we send that batch.
    const initialTimerMillis = DataLayer.getData().d2cVisitorToken ? 0 : this.batchingTimeMillis;
    this.timer = this.getAddToBatchTimer(initialTimerMillis);
    this.D2CClientRequestHandler = new D2CClientRequestHandler();
  }

  /**
   * Initializes the timer that controlls how long we wait between polling intervals
   * @param millis - How many milliseconds to wait between batches.  Negative numbers disable polling.
   * @return the timer, or undefined if there is none (for negative values of millis)
   */
  private getAddToBatchTimer(millis: number): ReturnType<typeof setTimeout> | undefined {
    if (millis >= 0) {
      return setTimeout(
        function () {
          this.addToBatch(false);
        }.bind(this),
        millis
      );
    }
  }

  /**
   * Removes events from the DataLayer, places them in batches, and sends them to the API via the request handler
   * @param lastCall - Whether this is a final call (use keepalive flag, no retries)
   */
  addToBatch(lastCall: boolean): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    // grab events
    const events = DataLayer.getData().events as Array<JSONObject>;
    if (events.length === 0) {
      this.timer = this.getAddToBatchTimer(this.batchingTimeMillis);

      return;
    }

    // set up batch objects
    const batches = [this.createBatch()];

    // place events into batches
    let curBatchIdx = 0;
    let curBatchSize = 0;

    while (events.length > 0) {
      const event = events.shift();

      if (event) {
        if (curBatchIdx >= batches.length) {
          batches.push(this.createBatch());
        }

        batches[curBatchIdx].d2cVisitorToken = DataLayer.getData().d2cVisitorToken as string;
        batches[curBatchIdx].batchEvents.push(event);

        curBatchSize++;
        if (curBatchSize >= this.maxBatchSize) {
          curBatchIdx++;
          curBatchSize = 0;
        }
      }
    }

    // fire off API calls for batches
    for (let i = 0; i < batches.length; i++) {
      const inflightBatch = {
        batch: batches[i],
        retries: 0,
      };
      this.sendBatch(inflightBatch, lastCall);
    }

    // run this method again in batchingTimeMillis milliseconds
    if (!lastCall) {
      this.timer = this.getAddToBatchTimer(this.batchingTimeMillis);
    }
  }

  /**
   * A utility function for initializing batches
   * @return val - a new batch with a random UUID
   */
  createBatch(): Batch {
    return {
      batchId: v4(),
      schemaVersion: schemaVersion,
      batchEvents: [],
      d2cVisitorToken: (DataLayer.getCommonData().d2cVisitorToken as string) || '',
    };
  }

  /**
   * Actually sends the batch.  Has the keepalive flag set if lastCall is set to true.
   * @param inflight - The batch to send
   * @param lastCall - Whether this is a final call (use keepalive flag, no retries)
   * @return val - a Promise for the response
   */
  private async sendBatch(inflight: InflightBatch, lastCall: boolean): Promise<void> {
    const requestOptions = {
      batch: inflight.batch,
      keepalive: lastCall,
    };
    try {
      const response = await this.D2CClientRequestHandler.sendBatch(BATCH_MANAGER_ENDPOINT, requestOptions, this.maxRetries);
      if (response && !response.ok) {
        this.failedBatchQueue.push(inflight.batch);
      }

      log(`Sending batch: ${inflight.batch}, and ${!lastCall ? 'no ' : ''}keepalive flag.`);
    } catch (e) {
      this.failedBatchQueue.push(inflight.batch);
      logErr(e);
    }
  }

  /**
   * Sends all remaining events to the API and clears out timers
   */
  flushBatchQueue(): void {
    if (this.timer) {
      clearTimeout(this.timer); // we call this immediately to minimize race conditions
    }

    this.addToBatch(true);
  }
}
